import React, { Children, cloneElement, forwardRef, type ReactNode, type Ref } from "react";
import { Heading, type HeadingTagType } from "@purpur/heading";
import * as RadixRadioGroup from "@radix-ui/react-radio-group";

import { cx } from "./classnames";
import { isRadioCardItem } from "./is-radio-card-item";
import type { RadioCardGroupOrientation, RadioPosition } from "./radio-card-group.constants";
import { RadioCardItem, type RadioCardItemProps } from "./radio-card-item";

export type RadioCardGroupItem = RadioCardItemProps & { ref?: Ref<HTMLDivElement> };

type StylingProps = Pick<React.HTMLAttributes<HTMLElement>, "className" | "style">;

export type RadioCardGroupProps = StylingProps & {
  /**
   * To use when no heading is given.
   * */
  ["aria-label"]?: string;
  /**
   * To use with custom label.
   * */
  ["aria-labelledby"]?: string;
  ["data-testid"]?: string;
  /**
   * The value of the radio card item that should be checked when initially rendered. Use when you do not need to control the state of the radio items.
   * */
  defaultValue?: string;
  /**
   * When true, prevents the user from interacting with radio card items.
   * */
  disabled?: boolean;
  /**
   * ID of the radio card group container.
   * */
  id: string;
  /**
   * The radio card items.
   * */
  items?: RadioCardGroupItem[];
  /**
   * Renders above the radio card group as a heading
   * */
  heading?: string;
  /**
   * Changes heading level for heading.
   */
  headingTag?: HeadingTagType;
  /**
   * When true, keyboard navigation will loop from last item to first, and vice versa.
   * */
  loop?: boolean;
  /**
   * The name of the group. Submitted with its owning form as part of a name/value pair.
   * */
  name?: string;
  /**
   * Event handler called when the value changes.
   * */
  onValueChange?: (value: string) => void;
  /**
   * Set to stack cards horizontally or vertically. When `horizontal`, the min card width with is 100% when container is sm, and `--purpur-breakpoint-lg` / 4 when the container is md or larger.
   * */
  orientation?: RadioCardGroupOrientation;
  /**
   * The position if the radio in the card items.
   * */
  radioPosition?: RadioPosition;
  /**
   * When true, indicates that the user must check a radio card item before the owning form can be submitted.
   * */
  required?: boolean;
  /**
   * The controlled value of the radio card item to check. Should be used in conjunction with onValueChange.
   * */
  value?: string;
  /**
   * Will only render given RadioCardItems.
   * */
  children?: ReactNode;
};

const rootClassName = "purpur-radio-card-group";

export const RadioCardGroup = forwardRef<HTMLDivElement, RadioCardGroupProps>(
  (
    {
      "data-testid": dataTestId,
      "aria-labelledby": ariaLabelledby,
      children,
      className,
      items,
      heading,
      headingTag = "h2",
      loop = true,
      orientation = "vertical",
      radioPosition = "right",
      ...props
    },
    ref
  ) => {
    const radioCardItemChildren = Children.toArray(children).filter(isRadioCardItem);

    return (
      <div className={cx(className, `${rootClassName}__container`)} ref={ref}>
        {heading && (
          <Heading
            data-testid={dataTestId ? `${dataTestId}-heading` : undefined}
            id={`${props.id}-heading`}
            tag={headingTag}
            variant="subsection-100"
          >
            {props.required && <span aria-hidden>{"* "}</span>}
            {heading}
          </Heading>
        )}
        {(!!items?.length || radioCardItemChildren.length) && (
          <RadixRadioGroup.Root
            {...props}
            aria-labelledby={ariaLabelledby || (heading ? `${props.id}-heading` : undefined)}
            className={cx(
              rootClassName,
              `${rootClassName}--${orientation}`,
              `${rootClassName}--radio-${radioPosition}`
            )}
            data-testid={dataTestId}
            loop={loop}
          >
            {items?.map((item) => (
              <RadioCardItem
                key={item.id}
                {...item}
                disabled={item.disabled || props.disabled}
                data-testid={dataTestId ? `${dataTestId}-item` : undefined}
              />
            ))}
            {Children.map(radioCardItemChildren, (child) =>
              cloneElement(child, { disabled: child.props.disabled || props.disabled })
            )}
          </RadixRadioGroup.Root>
        )}
      </div>
    );
  }
);

RadioCardGroup.displayName = "RadioCardGroup";

// RadioCardItem must be exported after `RadioCardGroup` for the Storybook controls to be generated from RadioCardGroup props
export type { RadioCardItemProps } from "./radio-card-item";
export { RadioCardItem } from "./radio-card-item";
